import { useRef, useState } from 'react'
import { Form, InputNumber, Radio, Upload, Tooltip, message } from 'antd'
import type { FormInstance } from 'antd'
import {
  Plus,
  Trash2,
  Upload as UploadIcon,
  Loader2,
  AlertCircle,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'
import dayjs from 'dayjs'
import { MediaPreviewModal } from '@/pages/events/MediaPreviewModal'
import {
  PILL_INPUT,
  PILL_SELECT,
  SECTION_CARD,
  SECTION_TITLE,
  FIELD_ITEM_LABEL,
} from './RobotForm'
import { Button } from '../ui/button'
import { uploadMultipartFile } from '@/api/file.api'

export interface EventPlayItem {
  id?: string
  video?: any
  image?: any
  audio?: any
}

export interface EventFormValues {
  id?: string
  code?: string
  eventType: number
  name: string
  startDate: dayjs.Dayjs | null
  endDate?: dayjs.Dayjs | null
  description?: string
  mediaType: number
  mediaShowtime?: string | number
  displayUnit?: string
  items?: EventPlayItem[]
}

interface EventFormProps {
  form: FormInstance<EventFormValues>
  mode: 'create' | 'update'
  onSubmit: (values: EventFormValues) => void
}

const getValueFromUpload = (e: any) => {
  if (Array.isArray(e)) {
    return e
  }
  return e && e.fileList ? e.fileList : []
}

export function EventForm({ form, mode, onSubmit }: EventFormProps) {
  const [previewFile, setPreviewFile] = useState<{
    url: string
    name: string
    type?: number
  } | null>(null)

  const itemsWatch = Form.useWatch('items', form)
  const mediaType = Form.useWatch('mediaType', form) || 1
  const initialDateRef = useRef<{
    startDate?: dayjs.Dayjs | null
    endDate?: dayjs.Dayjs | null
  } | null>(null)

  const startDateVal = Form.useWatch('startDate', form)
  const endDateVal = Form.useWatch('endDate', form)

  if (
    mode === 'update' &&
    (startDateVal || endDateVal) &&
    !initialDateRef.current
  ) {
    initialDateRef.current = {
      startDate: startDateVal,
      endDate: endDateVal,
    }
  }

  const hasDateChanged = (
    currentVal?: dayjs.Dayjs | null,
    initialVal?: dayjs.Dayjs | null,
  ) => {
    if (mode !== 'update') return true
    if (!currentVal && !initialVal) return false
    if (!currentVal || !initialVal) return true
    return !currentVal.isSame(initialVal, 'day')
  }

  const handleCustomUpload = async ({ file, onSuccess, onError }: any) => {
    try {
      const uploadedId = await uploadMultipartFile(file as File, 'media-file')

      file.id = uploadedId
      file.uploadedFileName = file.name || file.originFileObj?.name
      file.response = { id: uploadedId, name: file.name }

      onSuccess({ id: uploadedId, name: file.name }, file)
    } catch (error) {
      message.error('Tải tệp lên thất bại!')
      onError(error)
    }
  }

  const renderUploadCell = (fileListVal: any) => {
    const fileList = Array.isArray(fileListVal)
      ? fileListVal
      : fileListVal?.fileList || []
    const file = fileList.length > 0 ? fileList[fileList.length - 1] : null

    if (!file) {
      return (
        <div className="flex items-center gap-2 cursor-pointer py-1">
          <UploadIcon size={16} className="text-gray-500 shrink-0" />
          <span className="text-gray-400 italic text-sm">Tải lên tệp</span>
        </div>
      )
    }

    const isUploading = file.status === 'uploading'

    if (isUploading) {
      return (
        <div className="flex items-center gap-2 cursor-pointer py-1">
          <Loader2 size={16} className="animate-spin text-gray-500 shrink-0" />
          <span className="text-gray-400 italic text-sm">Đang tải lên...</span>
        </div>
      )
    }

    const fileName =
      file.uploadedFileName ||
      file.name ||
      file.originFileName ||
      file.originFileObj?.name ||
      file.response?.name

    return (
      <div className="flex items-center gap-2 cursor-pointer py-1">
        <UploadIcon size={16} className="text-[#0E6DD2] shrink-0" />
        {fileName ? (
          <span
            className="text-[#0E6DD2] italic font-normal truncate max-w-[200px]"
            title={fileName}
          >
            {fileName}
          </span>
        ) : (
          <span className="text-gray-400 italic text-sm">Tải lên tệp</span>
        )}
      </div>
    )
  }

  const handleUploadChange = (
    fieldName: number,
    fieldType: 'video' | 'image' | 'audio',
    info: any,
  ) => {
    const currentItems = form.getFieldValue('items') || []
    const updatedItems = [...currentItems]

    if (updatedItems[fieldName]) {
      updatedItems[fieldName] = {
        ...updatedItems[fieldName],
        [fieldType]: info.fileList,
      }
      form.setFieldsValue({ items: updatedItems })
    }
  }

  // Content hiển thị Tooltip
  const renderTooltipContent = () => {
    if (mediaType === 1) {
      return (
        <div className="text-xs text-[#1E2F41] leading-relaxed p-1 space-y-1">
          <div className="font-bold text-sm mb-1.5 text-[#1E2F41]">Lưu ý:</div>
          <div className="flex items-start gap-1.5">
            <span>•</span>
            <span>
              Các tệp tải lên không vượt quá <strong>200MB</strong>
            </span>
          </div>
          <div className="flex items-start gap-1.5">
            <span>•</span>
            <span>
              Tải lên video có định dạng <strong>.mp4</strong>,{' '}
              <strong>.mov</strong>, <strong>.mkv</strong>,{' '}
              <strong>.avi</strong>, <strong>.webm</strong>
            </span>
          </div>
        </div>
      )
    }

    return (
      <div className="text-xs text-[#1E2F41] leading-relaxed p-1 space-y-1">
        <div className="font-bold text-sm mb-1.5 text-[#1E2F41]">Lưu ý:</div>
        <div className="flex items-start gap-1.5">
          <span>•</span>
          <span>
            Các tệp tải lên không vượt quá <strong>200MB</strong>
          </span>
        </div>
        <div className="flex items-start gap-1.5">
          <span>•</span>
          <span>
            Tải lên ảnh có kích thước <strong>1920x1080</strong> với định dạng{' '}
            <strong>.jpg</strong>, <strong>.jpeg</strong>, <strong>.png</strong>
            , <strong>.webp</strong>, <strong>.svg</strong>
          </span>
        </div>
        <div className="flex items-start gap-1.5">
          <span>•</span>
          <span>
            Tải lên audio với định dạng <strong>.mp3</strong>,{' '}
            <strong>.wav</strong>, <strong>.flac</strong>, <strong>.m4a</strong>
            , <strong>.aac</strong>
          </span>
        </div>
      </div>
    )
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        initialValues={{
          mediaType: 1,
          eventType: 1,
          mediaShowtime: 'Mặc định theo thời gian phát của video',
          displayUnit: 'Giây',
          items: [{}],
        }}
        requiredMark={(label, info) => (
          <>
            <span className="font-semibold text-[#344054]">{label}</span>
            {info.required && <span className="text-red-500 ml-1">*</span>}
          </>
        )}
        className="space-y-6"
      >
        {/* THÔNG TIN CHUNG */}
        <div className={SECTION_CARD}>
          <h3 className={SECTION_TITLE}>Thông tin chung</h3>

          <Form.Item
            name="name"
            label="Tên sự kiện"
            className={FIELD_ITEM_LABEL}
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Vui lòng nhập tên sự kiện',
              },
            ]}
          >
            <Input
              placeholder="Nhập tên sự kiện"
              size="large"
              className={PILL_INPUT}
            />
          </Form.Item>

          <Form.Item
            name="startDate"
            label="Ngày bắt đầu"
            className={FIELD_ITEM_LABEL}
            dependencies={['endDate']}
            getValueFromEvent={(date: dayjs.Dayjs | null) =>
              date ? date.startOf('day') : null
            }
            rules={[
              { required: true, message: 'Vui lòng chọn ngày bắt đầu' },
              () => ({
                validator(_, value) {
                  if (!value) return Promise.resolve()
                  const today = dayjs().startOf('day')
                  const isChanged = hasDateChanged(
                    value,
                    initialDateRef.current?.startDate,
                  )

                  if (isChanged && value.isBefore(today, 'day')) {
                    return Promise.reject(
                      new Error(
                        'Ngày bắt đầu không được nhỏ hơn ngày hiện tại',
                      ),
                    )
                  }
                  return Promise.resolve()
                },
              }),
            ]}
          >
            <DatePicker
              placeholder="DD/MM/YYYY"
              format="DD/MM/YYYY"
              size="large"
              className={`w-full ${PILL_INPUT}`}
            />
          </Form.Item>

          <Form.Item
            name="endDate"
            label="Ngày kết thúc"
            className={FIELD_ITEM_LABEL}
            dependencies={['startDate']}
            getValueFromEvent={(date: dayjs.Dayjs | null) =>
              date ? date.endOf('day').millisecond(0) : null
            }
            rules={[
              { required: true, message: 'Vui lòng chọn ngày kết thúc' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value) return Promise.resolve()
                  const today = dayjs().startOf('day')
                  const isChanged = hasDateChanged(
                    value,
                    initialDateRef.current?.endDate,
                  )
                  if (isChanged && value.isBefore(today, 'day')) {
                    return Promise.reject(
                      new Error(
                        'Ngày kết thúc không được nhỏ hơn ngày hiện tại',
                      ),
                    )
                  }
                  const startDate = getFieldValue('startDate')
                  if (startDate && value.isBefore(startDate, 'day')) {
                    return Promise.reject(
                      new Error(
                        'Ngày kết thúc không được nhỏ hơn ngày bắt đầu',
                      ),
                    )
                  }
                  return Promise.resolve()
                },
              }),
            ]}
          >
            <DatePicker
              placeholder="DD/MM/YYYY"
              format="DD/MM/YYYY"
              size="large"
              className={`w-full ${PILL_INPUT}`}
            />
          </Form.Item>
        </div>

        {/* THÔNG TIN CHI TIẾT */}
        <div className={SECTION_CARD}>
          <h3 className={SECTION_TITLE}>Thông tin chi tiết</h3>

          <Form.Item
            name="mediaType"
            label="Loại nội dung"
            className={`mb-4 ${FIELD_ITEM_LABEL}`}
          >
            <Radio.Group
              className="flex flex-col gap-2.5"
              onChange={(e) => {
                const val = e.target.value
                if (val === 1) {
                  form.setFieldValue(
                    'mediaShowtime',
                    'Mặc định theo thời gian phát của video',
                  )
                } else {
                  form.setFieldValue('mediaShowtime', '')
                }
              }}
            >
              <Radio value={1}>Video</Radio>
              <Radio value={2}>Tuyên truyền</Radio>
            </Radio.Group>
          </Form.Item>

          <div className="grid grid-cols-[1fr_128px] gap-3">
            <Form.Item
              name="mediaShowtime"
              label="Thời gian hiển thị"
              className={`mb-3 ${FIELD_ITEM_LABEL}`}
              rules={[
                {
                  required: mediaType === 2,
                  message: 'Vui lòng nhập thời gian hiển thị',
                },
              ]}
            >
              {mediaType === 1 ? (
                <Input
                  disabled
                  placeholder="Mặc định theo thời gian phát của video"
                  className={`${PILL_INPUT} !bg-[#1E2F410D] !h-11`}
                />
              ) : (
                <InputNumber
                  min={1}
                  placeholder="Nhập số"
                  className={`${PILL_INPUT} w-full !h-11`}
                />
              )}
            </Form.Item>
            <Form.Item
              name="displayUnit"
              label="Đơn vị"
              className={`mb-3 ${FIELD_ITEM_LABEL}`}
            >
              <Select
                options={[{ label: 'Giây', value: 'Giây' }]}
                disabled
                className={`${PILL_SELECT} !bg-[#1E2F410D] h-11`}
              />
            </Form.Item>
          </div>

          {/* Table Form.List */}
          <div className="mt-2">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="text-base font-semibold text-[#344054]">
                Nội dung
              </span>
              <span className="text-red-500">*</span>
              <Tooltip
                title={renderTooltipContent()}
                placement="bottomLeft"
                color="#FFFFFF"
                styles={{
                  container: {
                    borderRadius: '16px',
                    padding: '12px 16px',
                    boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.12)',
                    maxWidth: '340px',
                  },
                }}
              >
                <div className="inline-flex items-center justify-center cursor-pointer text-[#1E2F41] hover:text-[#0E6DD2] transition-colors ml-0.5">
                  <AlertCircle size={18} strokeWidth={2} />
                </div>
              </Tooltip>
            </div>

            <Form.List name="items">
              {(fields, { add, remove }) => (
                <div className="space-y-3">
                  <div className="border border-[rgba(30,47,65,0.25)] rounded-2xl overflow-hidden">
                    <div className="bg-[#F8F9FA] border-b border-[rgba(30,47,65,0.15)] py-3 px-4 text-xs font-bold text-[#1e2f41] uppercase flex items-center">
                      <div className="w-12 text-left pl-2">STT</div>
                      {mediaType === 1 ? (
                        <div className="flex-1">
                          VIDEO <span className="text-red-500">*</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex-1">
                            HÌNH ẢNH <span className="text-red-500">*</span>
                          </div>
                          <div className="flex-1">ÂM THANH</div>
                        </>
                      )}
                      <div className="w-24 text-right pr-2">HÀNH ĐỘNG</div>
                    </div>

                    <div className="divide-y divide-gray-100 bg-white">
                      {fields.map((field, index) => {
                        const { key, ...restField } = field
                        const currentItem = itemsWatch?.[field.name]

                        return (
                          <div
                            key={key}
                            className="py-3 px-4 flex items-center min-h-[52px]"
                          >
                            <div className="w-12 text-left pl-2 text-[#344054] font-medium">
                              {index + 1}
                            </div>

                            {/* Cột Video */}
                            {mediaType === 1 && (
                              <div className="flex-1 pr-4">
                                <Form.Item
                                  {...restField}
                                  name={[field.name, 'video']}
                                  valuePropName="fileList"
                                  getValueFromEvent={getValueFromUpload}
                                  noStyle
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Vui lòng chọn video',
                                    },
                                  ]}
                                >
                                  <Upload
                                    accept=".mp4,.mov,.mkv,.avi,.webm"
                                    maxCount={1}
                                    showUploadList={false}
                                    customRequest={handleCustomUpload}
                                    onChange={(info) =>
                                      handleUploadChange(
                                        field.name,
                                        'video',
                                        info,
                                      )
                                    }
                                  >
                                    {renderUploadCell(currentItem?.video)}
                                  </Upload>
                                </Form.Item>
                              </div>
                            )}

                            {/* Cột Hình ảnh & Âm thanh */}
                            {mediaType === 2 && (
                              <>
                                <div className="flex-1 pr-4 border-r border-gray-100">
                                  <Form.Item
                                    {...restField}
                                    name={[field.name, 'image']}
                                    valuePropName="fileList"
                                    getValueFromEvent={getValueFromUpload}
                                    noStyle
                                    rules={[
                                      {
                                        required: true,
                                        message: 'Vui lòng chọn hình ảnh',
                                      },
                                    ]}
                                  >
                                    <Upload
                                      accept=".jpg,.jpeg,.png,.webp,.svg"
                                      maxCount={1}
                                      showUploadList={false}
                                      customRequest={handleCustomUpload}
                                      onChange={(info) =>
                                        handleUploadChange(
                                          field.name,
                                          'image',
                                          info,
                                        )
                                      }
                                    >
                                      {renderUploadCell(currentItem?.image)}
                                    </Upload>
                                  </Form.Item>
                                </div>

                                <div className="flex-1 pl-4 pr-4">
                                  <Form.Item
                                    {...restField}
                                    name={[field.name, 'audio']}
                                    valuePropName="fileList"
                                    getValueFromEvent={getValueFromUpload}
                                    noStyle
                                  >
                                    <Upload
                                      accept=".mp3,.wav,.flac,.m4a,.aac"
                                      maxCount={1}
                                      showUploadList={false}
                                      customRequest={handleCustomUpload}
                                      onChange={(info) =>
                                        handleUploadChange(
                                          field.name,
                                          'audio',
                                          info,
                                        )
                                      }
                                    >
                                      {renderUploadCell(currentItem?.audio)}
                                    </Upload>
                                  </Form.Item>
                                </div>
                              </>
                            )}

                            <div className="w-24 flex justify-end pr-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled={fields.length <= 1}
                                onClick={() => remove(field.name)}
                              >
                                <Trash2 size={18} />
                              </Button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => add()}
                    className="border-[2px] border-[#CE7A58]/50 text-[#CE7A58]/50 hover:bg-[#CE7A58]"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </Form.List>
          </div>
        </div>
      </Form>

      <MediaPreviewModal
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </>
  )
}
